import { Component, Input, OnInit, OnChanges, SimpleChanges, ViewEncapsulation, AfterViewInit, ElementRef, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as d3 from 'd3';
import { ThemeService } from '../../../../core/services/theme.service';

export interface StackedBarData {
  label: string;
  // legacy backward compatibility
  scheduled?: number;
  recovered?: number;
  // preferred properties
  bottomValue?: number;
  topValue?: number;
}

@Component({
  selector: 'app-stacked-bar',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './stacked-bar.component.html',
  host: {
    'class': 'w-full h-full block'
  },
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [`
    :host {
      display: block;
      width: 100%;
      height: 100%;
    }
    .stacked-bar-svg {
      width: 100%;
      height: 100%;
    }
  `]
})
export class StackedBarComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() data: StackedBarData[] = [];
  @Input() width: number | 'auto' = 800;
  @Input() height: number | 'auto' = 300;

  // Legacy input compatibility
  @Input() scheduledColor: string = '#ef4444';
  @Input() recoveredColor: string = '#10b981';

  // New preferred inputs
  @Input() bottomColor?: string;
  @Input() topColor?: string;
  @Input() bottomLabel: string = 'Scheduled';
  @Input() topLabel: string = 'Recovered';
  @Input() useStripesForTop: boolean = false;
  @Input() hoverOpacity: number = 0.05;
  @Input() showBgRect: boolean = true;

  // New Controls
  @Input() showXAxis: boolean = true;
  @Input() showYAxis: boolean = true;
  @Input() padding: number = 0.6; // Control bar thickness
  @Input() legendMarginTop: number = 0; // Additional margin between chart and legend
  @Input() totalLabel: string = 'Total';
  @Input() showTotalTooltip: boolean = true;
  @Input() xAxisShowOnlyFirstLast: boolean = false;
  @Input() xAxisPadding: number = 0;
  @Input() margins: any = {};

  readonly chartId = `stacked-bar-${Math.random().toString(36).substring(2, 9)}`;

  private actualWidth: number = 0;
  private actualHeight: number = 0;
  private resizeObserver?: ResizeObserver;

  constructor(private themeService: ThemeService, private el: ElementRef) { }

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    this.setupResizeObserver();
    setTimeout(() => {
      this.renderChart();
    }, 0);
  }

  private setupResizeObserver(): void {
    this.resizeObserver = new ResizeObserver(() => {
      this.renderChart();
    });
    this.resizeObserver.observe(this.el.nativeElement);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && !changes['data'].firstChange) {
      this.renderChart();
    }
    // Also re-render if other inputs change
    if (changes['showXAxis'] || changes['showYAxis'] || changes['padding'] || changes['legendMarginTop'] || changes['xAxisPadding']) {
      this.renderChart();
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  private renderChart(): void {
    const container = d3.select(`#${this.chartId}`);
    container.selectAll('*').remove();

    if (!this.data || this.data.length === 0) return;

    const isDark = this.themeService.getTheme() === 'dark';
    const textColor = isDark ? '#a1a1aa' : '#64748b'; // slate-400 / slate-500
    const axisColor = isDark ? '#47474e' : '#cbd5e1'; // slate-600 / slate-300
    const gridColor = isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)';

    const rect = this.el.nativeElement.getBoundingClientRect();
    this.actualWidth = rect.width || this.el.nativeElement.clientWidth || (typeof this.width === 'number' ? this.width : 400);
    this.actualHeight = rect.height || this.el.nativeElement.clientHeight || (typeof this.height === 'number' ? this.height : 300);

    const width = this.actualWidth;
    const height = this.actualHeight;

    const baseBottomMargin = this.showXAxis ? 40 : 10;
    const marginBottom = baseBottomMargin + this.legendMarginTop;

    const margin = { 
      top: this.margins?.top ?? 20, 
      right: this.margins?.right ?? 20, 
      bottom: this.margins?.bottom ?? marginBottom, 
      left: this.margins?.left ?? 50 
    };
    const chartWidth = width - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;

    const svg = container
      .append('svg')
      .attr('class', 'stacked-bar-svg')
      .attr('width', '100%')
      .attr('height', '100%')
      .attr('viewBox', `0 0 ${width} ${height}`)
      .attr('preserveAspectRatio', 'xMidYMid meet');

    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Scales
    const x = d3.scaleBand()
      .domain(this.data.map(d => d.label))
      .range([this.xAxisPadding, chartWidth])
      .padding(this.padding);

    // Helper to extract values backward compatibility
    const getBottom = (d: StackedBarData) => d.bottomValue !== undefined ? d.bottomValue : (d.scheduled || 0);
    const getTop = (d: StackedBarData) => d.topValue !== undefined ? d.topValue : (d.recovered || 0);

    const maxValue = d3.max(this.data, (d: StackedBarData) => getBottom(d) + getTop(d)) || 1;
    const y = d3.scaleLinear()
      .domain([0, maxValue * 1.1])
      .range([chartHeight, 0]);

    // Define patterns
    const defs = svg.append('defs');

    // Resolve colors for backward compatibility
    const actualBottomColor = this.bottomColor || this.scheduledColor;
    const actualTopColor = this.topColor || this.recoveredColor;

    if (this.useStripesForTop) {
      // Restore vertical stripe pattern
      const stripePattern = defs.append('pattern')
        .attr('id', `stripe-${this.chartId}`)
        .attr('width', 4)
        .attr('height', 4)
        .attr('patternUnits', 'userSpaceOnUse');

      stripePattern.append('line')
        .attr('x1', 0)
        .attr('y1', 0)
        .attr('x2', 0)
        .attr('y2', 4)
        .attr('stroke', actualTopColor)
        .attr('stroke-width', 1.5);
    }

    // Gridlines
    if (this.showYAxis) {
      g.append('g')
        .attr('class', 'grid')
        .call(d3.axisLeft(y)
          .tickSize(-chartWidth)
          .ticks(5)
        )
        .call(g => g.select('.domain').remove())
        .call(g => g.selectAll('.tick line')
          .attr('stroke', gridColor)
          .attr('stroke-dasharray', '4,4')
        )
        .call(g => g.selectAll('.tick text').remove());
    }

    // X Axis
    if (this.showXAxis) {
      const xAxis = d3.axisBottom(x);
      if (this.xAxisShowOnlyFirstLast) {
        const domain = x.domain();
        if (domain.length > 1) {
          xAxis.tickValues([domain[0], domain[domain.length - 1]]);
        }
      }

      g.append('g')
        .attr('transform', `translate(0,${chartHeight})`)
        .call(xAxis)
        .call(g => g.select('.domain').attr('stroke', axisColor))
        .call(g => g.selectAll('.tick line').attr('stroke', axisColor))
        .selectAll('text')
        .attr('fill', textColor)
        .style('font-size', '12px')
        .style('font-family', 'Inter, sans-serif');
    }

    // Y Axis
    if (this.showYAxis) {
      g.append('g')
        .call(d3.axisLeft(y)
          .ticks(5)
          .tickFormat((d: any) => d >= 1000 ? `${(d / 1000).toFixed(1)}k` : d.toString())
        )
        .call(g => g.select('.domain').attr('stroke', axisColor))
        .call(g => g.selectAll('.tick line').attr('stroke', axisColor))
        .selectAll('text')
        .attr('fill', textColor)
        .style('font-size', '12px')
        .style('font-family', 'Inter, sans-serif');
    }

    // Bars
    const barWidth = x.bandwidth();
    const cornerRadius = 6;

    this.data.forEach(d => {
      const xPos = x(d.label)!;

      const bottomVal = getBottom(d);
      const topVal = getTop(d);

      const bottomY = y(bottomVal);
      const bottomHeight = chartHeight - bottomY;

      const topY = y(bottomVal + topVal);
      const topHeight = chartHeight - topY - bottomHeight;

      const topBlockHeight = y(bottomVal) - y(bottomVal + topVal);
      const topBlockY = y(bottomVal + topVal);

      // Tooltip Styles based on Theme
      const tooltipBg = isDark ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.95)';
      const tooltipColor = isDark ? '#fff' : '#1e293b'; // slate-800
      const tooltipBorder = isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0'; // slate-200
      const tooltipShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';

      const barGroup = g.append('g')
        .attr('class', 'bar-group')
        .attr('cursor', 'pointer');

      // Add background highlight rectangle
      const bgRect = barGroup.append('rect')
        .attr('x', xPos)
        .attr('y', 0)
        .attr('width', x.bandwidth())
        .attr('height', chartHeight)
        .attr('fill', isDark ? '#ffffff' : '#000000')
        .attr('fill-opacity', this.showBgRect ? this.hoverOpacity : 0)
        .attr('rx', 4)
        .style('display', this.showBgRect ? 'block' : 'none');

      barGroup.on('mouseover', (event) => {
        if (this.showBgRect) {
          bgRect.transition().duration(200).attr('fill-opacity', this.hoverOpacity * 2.5);
        }
          const tooltip = d3.select('body').selectAll('.stacked-tooltip').data([null]);
          const tooltipEnter = tooltip.enter().append('div')
            .attr('class', 'stacked-tooltip')
            .style('position', 'absolute')
            .style('pointer-events', 'none')
            .style('z-index', '1000')
            .style('opacity', 0);

          const t = tooltip.merge(tooltipEnter as any);

          t.html(`
             <div class="p-3 min-w-[200px]" style="background: ${isDark ? '#1e1e21' : '#ffffff'}; border-radius: 12px; font-family: 'Inter', sans-serif; box-shadow: ${tooltipShadow}; border: ${tooltipBorder};">
               <div class="text-sm font-bold mb-3 pb-2" style="color: ${tooltipColor}; border-bottom: 1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#f1f5f9'};">${d.label}</div>
               <div class="flex flex-col gap-2.5">
                 <div class="flex items-center justify-between gap-6">
                   <div class="flex items-center gap-2">
                     <div class="w-2.5 h-2.5 rounded-full" style="background-color: ${actualTopColor}"></div>
                     <span class="text-xs font-medium" style="color: ${isDark ? '#a1a1aa' : '#64748b'};">${this.topLabel}:</span>
                   </div>
                   <span class="text-xs font-bold" style="color: ${tooltipColor};">${topVal.toLocaleString()}</span>
                 </div>
                 <div class="flex items-center justify-between gap-6">
                   <div class="flex items-center gap-2">
                     <div class="w-2.5 h-2.5 rounded-full" style="background-color: ${actualBottomColor}"></div>
                     <span class="text-xs font-medium" style="color: ${isDark ? '#a1a1aa' : '#64748b'};">${this.bottomLabel}:</span>
                   </div>
                   <span class="text-xs font-bold" style="color: ${tooltipColor};">${bottomVal.toLocaleString()}</span>
                 </div>
                 ${this.showTotalTooltip ? `
                 <div class="mt-2 pt-2 flex items-center justify-between gap-6" style="border-top: 1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#f1f5f9'};">
                   <span class="font-bold text-xs" style="color: ${tooltipColor};">${this.totalLabel}:</span>
                   <span class="font-bold text-xs" style="color: ${tooltipColor};">${(bottomVal + topVal).toLocaleString()}</span>
                 </div>` : ''}
               </div>
             </div>
           `)
            .style('opacity', 1);
        })
        .on('mousemove', (event) => {
          const tooltip = d3.select('.stacked-tooltip');
          const tooltipNode = tooltip.node() as HTMLElement;
          if (!tooltipNode) return;
          
          const tooltipWidth = tooltipNode.getBoundingClientRect().width;
          const containerNode = container.node() as HTMLElement;
          if (!containerNode) return;
          
          let left = event.pageX + 10;
          if (left + tooltipWidth > window.innerWidth) {
            left = event.pageX - tooltipWidth - 10;
          }

          const tooltipHeight = tooltipNode.getBoundingClientRect().height;
          let top = event.pageY - 10;
          if (top < 0) {
            top = event.pageY + 10;
          } else if (top + tooltipHeight > window.innerHeight) {
            top = event.pageY - tooltipHeight - 10;
          }

          tooltip
            .style('left', left + 'px')
            .style('top', top + 'px');
        })
        .on('mouseout', () => {
          if (this.showBgRect) {
            bgRect.transition().duration(200).attr('fill-opacity', this.hoverOpacity);
          }
          d3.select('.stacked-tooltip').remove();
        });

      // Bottom bar (solid) - Rounded Bottom corners
      barGroup.append('path')
        .attr('d', this.roundedRect(xPos, bottomY, barWidth, bottomHeight, cornerRadius, false, false, true, true))
        .attr('fill', actualBottomColor);

      // Top bar - Rounded Top corners
      barGroup.append('path')
        .attr('d', this.roundedRect(xPos, topBlockY, barWidth, topBlockHeight, cornerRadius, true, true, false, false))
        .attr('fill', this.useStripesForTop ? `url(#stripe-${this.chartId})` : actualTopColor);
    });

    // Legend
    // Position: Bottom of the chart area + some spacing?
    // Or absolute position from bottom of SVG?
    // User wanted control over margin. 
    // If I used legendMarginTop to increase chart margin, effectively pushed chart up.
    // Legend is at bottom.
    // Let's place legend relative to the bottom of the SVG, adjusted by padding?
    // Or relative to the chart bottom?
    // Usually legend is outside the chart area.

    const legendY = this.actualHeight - 10;
    const legendX = chartWidth / 2 - 60; // Center relative to chart area

    // Top Legend
    if (this.useStripesForTop) {
      svg.append('rect')
        .attr('x', legendX - 15)
        .attr('y', legendY - 8)
        .attr('width', 16)
        .attr('height', 10)
        .attr('rx', 2)
        .attr('fill', `url(#stripe-${this.chartId})`);
    } else {
      svg.append('circle')
        .attr('cx', legendX - 7)
        .attr('cy', legendY - 3)
        .attr('r', 5)
        .attr('fill', actualTopColor);
    }

    svg.append('text')
      .attr('x', legendX + 8)
      .attr('y', legendY + 1)
      .attr('fill', '#94a3b8') // slate-400
      .style('font-size', '12px')
      .style('font-family', 'Inter, sans-serif')
      .text(this.topLabel);

    const legendGap = 130;

    // Bottom Legend
    svg.append('circle')
      .attr('cx', legendX + legendGap - 7)
      .attr('cy', legendY - 3)
      .attr('r', 5)
      .attr('fill', actualBottomColor);

    svg.append('text')
      .attr('x', legendX + legendGap + 8)
      .attr('y', legendY + 1)
      .attr('fill', '#94a3b8') // slate-400
      .style('font-size', '12px')
      .style('font-family', 'Inter, sans-serif')
      .text(this.bottomLabel);
  }

  private roundedRect(x: number, y: number, w: number, h: number, r: number, tl: boolean, tr: boolean, bl: boolean, br: boolean): string {
    let retval;
    retval = "M" + (x + r) + "," + y;
    retval += "h" + (w - 2 * r);

    if (tr) { retval += "a" + r + "," + r + " 0 0 1 " + r + "," + r; }
    else { retval += "h" + r; retval += "v" + r; }

    retval += "v" + (h - 2 * r);

    if (br) { retval += "a" + r + "," + r + " 0 0 1 " + -r + "," + r; }
    else { retval += "v" + r; retval += "h" + -r; }

    retval += "h" + -(w - 2 * r);

    if (bl) { retval += "a" + r + "," + r + " 0 0 1 " + -r + "," + -r; }
    else { retval += "h" + -r; retval += "v" + -r; }

    retval += "v" + -(h - 2 * r);

    if (tl) { retval += "a" + r + "," + r + " 0 0 1 " + r + "," + -r; }
    else { retval += "v" + -r; retval += "h" + r; }

    retval += "z";
    return retval;
  }
}
